import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { accountApi, ApiError, api } from '../../lib/api'
import { useAuth } from '../../lib/auth'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia('(max-width: 760px)').matches,
  )
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 760px)')
    const onChange = () => setIsMobile(mql.matches)
    onChange()
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return isMobile
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

function SaveBar({
  state,
  label = 'Enregistrer cette section',
  error,
  onSave,
}: {
  state: SaveState
  label?: string
  error?: string | null
  onSave: () => void
}) {
  return (
    <div className="os-savebar">
      <button
        type="button"
        className="btn-primary btn-sm"
        onClick={onSave}
        disabled={state === 'saving'}
      >
        {state === 'saving' ? 'Enregistrement…' : label}
      </button>
      <span
        className={`os-saved ${state === 'saved' ? 'os-saved-on' : ''}`}
        role="status"
        aria-live="polite"
      >
        Enregistré ✓
      </span>
      {state === 'error' && error && <span className="os-saveerr">{error}</span>}
    </div>
  )
}

function Section({
  id,
  eyebrow,
  title,
  sub,
  isMobile,
  defaultOpen,
  children,
}: {
  id: string
  eyebrow: string
  title: string
  sub?: string
  isMobile: boolean
  defaultOpen?: boolean
  children: ReactNode
}) {
  if (isMobile) {
    return (
      <details
        id={`sec-${id}`}
        className="os-sec os-sec-collapsible"
        open={defaultOpen ? true : undefined}
      >
        <summary className="os-sec-sum">
          <span className="os-sec-eyebrow">{eyebrow}</span>
          <span className="os-sec-title-m">{title}</span>
          <span className="os-sec-chev" aria-hidden="true">
            <svg viewBox="0 0 12 12" width="12" height="12">
              <path
                d="M3 4.5 L6 7.5 L9 4.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </summary>
        <div className="os-sec-body">
          {sub && <p className="os-sec-sub">{sub}</p>}
          {children}
        </div>
      </details>
    )
  }

  return (
    <section id={`sec-${id}`} className="os-sec">
      <header className="os-sec-head">
        <p className="os-sec-eyebrow">{eyebrow}</p>
        <h2 className="os-sec-title">{title}</h2>
      </header>
      <div className="os-sec-body">
        {sub && <p className="os-sec-sub">{sub}</p>}
        {children}
      </div>
    </section>
  )
}

function fmtDate(ts: number) {
  return new Date(ts).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  })
}

/** React 19 blocks `javascript:` in JSX `href`; bookmarklets must use `setAttribute` after commit. */
function BookmarkletDragAnchor({
  bookmarkletHref,
  className,
  style,
  children,
}: {
  bookmarkletHref: string
  className?: string
  style?: CSSProperties
  children: ReactNode
}) {
  const ref = useRef<HTMLAnchorElement>(null)

  useLayoutEffect(() => {
    ref.current?.setAttribute('href', bookmarkletHref)
  })

  return (
    <a
      ref={ref}
      href="#"
      className={className}
      style={style}
      draggable
      onClick={(e) => {
        e.preventDefault()
      }}
      title="Glissez ce lien vers la barre des favoris pour installer le bookmarklet."
    >
      {children}
    </a>
  )
}

export default function Account() {
  const auth = useAuth()
  const nav = useNavigate()
  const isMobile = useIsMobile()

  const me = auth.status === 'authenticated' ? auth.me.user : null
  const email = me?.email ?? ''

  const [displayName, setDisplayName] = useState(me?.displayName ?? '')
  const [saveProfileState, setSaveProfileState] = useState<SaveState>('idle')
  const [profileErr, setProfileErr] = useState<string | null>(null)

  const [secBusy, setSecBusy] = useState(false)
  const [secMsg, setSecMsg] = useState<string | null>(null)

  const [tokens, setTokens] = useState<
    Array<{
      id: string
      name: string
      scopes: string[]
      lastUsedAt: number | null
      createdAt: number
    }>
  >([])
  const [tokensBusy, setTokensBusy] = useState(false)
  const [tokensErr, setTokensErr] = useState<string | null>(null)

  const [newTokenOpen, setNewTokenOpen] = useState(false)
  const [newTokenName, setNewTokenName] = useState('Automation')
  const [scopeItemsWrite, setScopeItemsWrite] = useState(true)
  const [scopeImagesWrite, setScopeImagesWrite] = useState(true)
  const [createdToken, setCreatedToken] = useState<{ name: string; token: string } | null>(null)

  const [bmBusy, setBmBusy] = useState(false)
  const [bmErr, setBmErr] = useState<string | null>(null)
  const [bmHref, setBmHref] = useState<string | null>(null)

  const [dangerOpen, setDangerOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteErr, setDeleteErr] = useState<string | null>(null)

  const refreshTokens = useCallback(async () => {
    setTokensBusy(true)
    setTokensErr(null)
    try {
      const rows = await accountApi.listTokens()
      setTokens(rows)
    } catch {
      setTokensErr('Impossible de charger vos jetons.')
    } finally {
      setTokensBusy(false)
    }
  }, [])

  useEffect(() => {
    setDisplayName(me?.displayName ?? '')
  }, [me?.displayName])

  useEffect(() => {
    void refreshTokens()
  }, [refreshTokens])

  function scheduleSaved(set: (s: SaveState) => void) {
    set('saved')
    window.setTimeout(() => set('idle'), 2200)
  }

  const deleteMatches = useMemo(
    () => deleteConfirm.trim().toLowerCase() === email.toLowerCase(),
    [deleteConfirm, email],
  )

  async function saveProfileAction() {
    const nextName = displayName.trim()
    setProfileErr(null)
    setSaveProfileState('saving')
    try {
      await accountApi.updateDisplayName(nextName)
      await auth.refresh()
      scheduleSaved(setSaveProfileState)
    } catch {
      setSaveProfileState('error')
      setProfileErr('Impossible de mettre à jour votre profil.')
      window.setTimeout(() => setSaveProfileState('idle'), 2600)
    }
  }

  async function logoutEverywhere() {
    setSecBusy(true)
    setSecMsg(null)
    try {
      await accountApi.logoutEverywhere()
      await auth.refresh()
      nav('/auth/sign-in')
    } catch {
      setSecMsg('Impossible de déconnecter toutes les sessions.')
    } finally {
      setSecBusy(false)
    }
  }

  async function revokeToken(id: string) {
    setTokensErr(null)
    setTokensBusy(true)
    try {
      await accountApi.revokeToken(id)
      await refreshTokens()
    } catch {
      setTokensErr('Impossible de révoquer ce jeton.')
    } finally {
      setTokensBusy(false)
    }
  }

  async function createToken() {
    setTokensErr(null)
    setTokensBusy(true)
    try {
      const scopes = [
        scopeItemsWrite ? 'items:write' : null,
        scopeImagesWrite ? 'images:write' : null,
      ].filter(Boolean) as Array<'items:write' | 'images:write'>
      if (!scopes.length) {
        setTokensErr('Choisissez au moins un scope.')
        return
      }
      const r = await accountApi.createToken(newTokenName.trim() || 'Jeton', scopes)
      setCreatedToken({ name: r.name, token: r.token })
      setNewTokenOpen(false)
      await refreshTokens()
    } catch {
      setTokensErr('Impossible de créer un jeton.')
    } finally {
      setTokensBusy(false)
    }
  }

  const bmToken = useMemo(() => {
    const want = new Set(['items:write', 'images:write'])
    const ok = (scopes: string[]) => Array.from(want).every((s) => scopes.includes(s))
    return tokens.find((t) => t.name.toLowerCase() === 'bookmarklet' && ok(t.scopes)) ?? null
  }, [tokens])

  async function buildBookmarkletHref(tokenId: string) {
    setBmBusy(true)
    setBmErr(null)
    try {
      const href = await accountApi.bookmarkletInstallHref(tokenId)
      setBmHref(href)
    } catch {
      setBmErr('Impossible de générer le bookmarklet. Réessayez dans un instant.')
    } finally {
      setBmBusy(false)
    }
  }

  async function generateBookmarklet() {
    setBmBusy(true)
    setBmErr(null)
    try {
      const t = await accountApi.createToken('Bookmarklet', ['images:write', 'items:write'])
      await refreshTokens()
      const href = await accountApi.bookmarkletInstallHref(t.id)
      setBmHref(href)
      setCreatedToken(null)
    } catch {
      setBmErr('Impossible de générer le bookmarklet. Vérifiez votre connexion et réessayez.')
    } finally {
      setBmBusy(false)
    }
  }

  async function revokeBookmarklet() {
    if (!bmToken) return
    setBmBusy(true)
    setBmErr(null)
    try {
      await accountApi.revokeToken(bmToken.id)
      setBmHref(null)
      await refreshTokens()
    } catch {
      setBmErr('Impossible de révoquer le bookmarklet.')
    } finally {
      setBmBusy(false)
    }
  }

  async function copyBookmarklet() {
    if (!bmHref) return
    try {
      await navigator.clipboard.writeText(bmHref)
    } catch {
      // Fallback: select + copy
      const el = document.getElementById('bm-href') as HTMLInputElement | null
      if (el) {
        el.focus()
        el.select()
        document.execCommand('copy')
      }
    }
  }

  async function deleteAccount() {
    if (!deleteMatches || deleteBusy) return
    setDeleteBusy(true)
    setDeleteErr(null)
    try {
      await accountApi.delete()
      await api.authLogout()
      await auth.refresh()
      nav('/')
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setDeleteErr('Impossible de supprimer le compte pour le moment.')
      } else {
        setDeleteErr('Suppression impossible.')
      }
    } finally {
      setDeleteBusy(false)
    }
  }

  if (auth.status === 'loading') return <main style={{ padding: '2rem' }}>Chargement…</main>
  if (auth.status === 'anonymous')
    return <main style={{ padding: '2rem' }}>Connexion requise.</main>

  return (
    <div className="owner-account os-page">
      <header className="os-chrome">
        <div className="os-chrome-inner">
          <div className="os-wordmark" aria-label="Liste de Naissance">
            <span className="os-wordmark-mark">·</span>
            <span className="os-wordmark-text">Liste de Naissance</span>
          </div>
          <nav className="os-bread" aria-label="Fil d'ariane">
            <Link to="/me" className="os-bread-link">
              Mes listes
            </Link>
            <span className="os-bread-sep" aria-hidden="true">
              /
            </span>
            <span className="os-bread-current">Mon compte</span>
          </nav>
        </div>
      </header>

      <div className="os-body">
        <main className="os-main">
          <div className="os-canvas">
            {isMobile && (
              <Link className="os-backlink" to="/me">
                ← Retour aux listes
              </Link>
            )}

            <header className="os-head">
              <p className="eyebrow">Compte</p>
              <h1 className="os-title">Vos informations.</h1>
              <p className="os-sub">
                Profil, sécurité, jetons d&apos;API, et suppression du compte.
              </p>
            </header>

            <Section
              id="profil"
              eyebrow="01"
              title="Profil"
              sub="Votre nom d'affichage (utilisé dans les invitations) et votre email."
              isMobile={isMobile}
              defaultOpen
            >
              <div className="os-grid2">
                <label className="field">
                  <span className="field-label">Nom</span>
                  <input
                    className="input"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Votre nom"
                    maxLength={120}
                  />
                </label>
                <label className="field">
                  <span className="field-label">Email</span>
                  <input className="input os-mono" value={email} readOnly />
                </label>
              </div>
              <SaveBar
                state={saveProfileState}
                error={profileErr}
                onSave={() => void saveProfileAction()}
              />
            </Section>

            <Section
              id="securite"
              eyebrow="02"
              title="Sécurité"
              sub="Révoquez toutes vos sessions en une fois."
              isMobile={isMobile}
            >
              <div className="os-danger-card">
                <div>
                  <h3 className="os-danger-title">Se déconnecter partout</h3>
                  <p className="os-danger-sub">
                    Déconnecte toutes vos sessions actives (ordinateur, mobile…) et vous redirige
                    vers la connexion.
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => void logoutEverywhere()}
                  disabled={secBusy}
                >
                  {secBusy ? 'Déconnexion…' : 'Se déconnecter de toutes les sessions'}
                </button>
              </div>
              {secMsg && <p className="field-error">{secMsg}</p>}
            </Section>

            <Section
              id="bookmarklet"
              eyebrow="03"
              title="Bookmarklet"
              sub="Ajoutez un cadeau depuis n'importe quelle page produit."
              isMobile={isMobile}
            >
              <div className="os-callout">
                <span className="os-callout-mark" aria-hidden="true">
                  i
                </span>
                <div>
                  <p style={{ margin: 0 }}>
                    Le bookmarklet s&apos;utilise depuis n&apos;importe quelle page produit :
                    cliquez, puis le cadeau est ajouté à votre liste.
                  </p>
                  <p style={{ margin: '10px 0 0' }}>
                    Astuce : affichez la barre des favoris dans votre navigateur pour le
                    glisser-déposer facilement.
                  </p>
                </div>
              </div>

              {!bmToken ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => void generateBookmarklet()}
                  disabled={bmBusy}
                >
                  {bmBusy ? 'Génération…' : 'Générer mon bookmarklet'}
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => void buildBookmarkletHref(bmToken.id)}
                    disabled={bmBusy}
                  >
                    {bmBusy ? 'Préparation…' : bmHref ? 'Regénérer' : 'Préparer'}
                  </button>
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => void revokeBookmarklet()}
                    disabled={bmBusy}
                  >
                    Révoquer
                  </button>
                </div>
              )}

              {bmErr && <p className="field-error">{bmErr}</p>}

              {bmToken && bmHref && (
                <div style={{ marginTop: 14 }}>
                  <p style={{ margin: '0 0 10px', color: 'var(--muted)' }}>
                    Glissez ce bouton dans vos favoris :
                  </p>
                  <BookmarkletDragAnchor
                    bookmarkletHref={bmHref}
                    className="btn-secondary"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 10,
                      userSelect: 'none',
                    }}
                  >
                    + Ajouter à Liste de naissance
                  </BookmarkletDragAnchor>
                  <div
                    style={{
                      marginTop: 12,
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => void copyBookmarklet()}
                    >
                      Copier le lien
                    </button>
                    <span className="os-mmail" style={{ margin: 0 }}>
                      Si le glisser-déposer ne marche pas, copiez puis collez dans un favori.
                    </span>
                  </div>
                  <p style={{ margin: '10px 0 0' }}>
                    <input
                      id="bm-href"
                      className="input os-mono"
                      readOnly
                      value={bmHref}
                      onFocus={(e) => e.currentTarget.select()}
                    />
                  </p>
                </div>
              )}
            </Section>

            <Section
              id="tokens"
              eyebrow="04"
              title="Jetons d'API"
              sub="Pour automatiser l'ajout d'images et de cadeaux (ex: bookmarklet, scripts)."
              isMobile={isMobile}
            >
              {createdToken && (
                <div className="os-callout" role="status" aria-live="polite">
                  <span className="os-callout-mark" aria-hidden="true">
                    ✓
                  </span>
                  <div>
                    <p style={{ margin: 0 }}>
                      Jeton créé : <strong>{createdToken.name}</strong>. Copiez-le maintenant — il
                      ne sera plus affiché.
                    </p>
                    <p style={{ margin: '8px 0 0' }}>
                      <input
                        className="input os-mono"
                        readOnly
                        value={createdToken.token}
                        onFocus={(e) => e.currentTarget.select()}
                      />
                    </p>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setNewTokenOpen((v) => !v)}
                  disabled={tokensBusy}
                >
                  {newTokenOpen ? 'Fermer' : 'Créer un jeton'}
                </button>
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => void refreshTokens()}
                  disabled={tokensBusy}
                >
                  {tokensBusy ? 'Actualisation…' : 'Actualiser'}
                </button>
              </div>

              {newTokenOpen && (
                <div className="os-danger-confirm" style={{ marginTop: 12 }}>
                  <div className="os-grid2">
                    <label className="field">
                      <span className="field-label">Nom</span>
                      <input
                        className="input"
                        value={newTokenName}
                        onChange={(e) => setNewTokenName(e.target.value)}
                        maxLength={80}
                      />
                    </label>
                    <div className="field">
                      <span className="field-label">Scopes</span>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="checkbox"
                          checked={scopeItemsWrite}
                          onChange={(e) => setScopeItemsWrite(e.target.checked)}
                        />
                        <span className="os-mono">items:write</span>
                      </label>
                      <label
                        style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}
                      >
                        <input
                          type="checkbox"
                          checked={scopeImagesWrite}
                          onChange={(e) => setScopeImagesWrite(e.target.checked)}
                        />
                        <span className="os-mono">images:write</span>
                      </label>
                    </div>
                  </div>
                  <div className="os-danger-actions" style={{ justifyContent: 'flex-start' }}>
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={() => void createToken()}
                    >
                      Créer
                    </button>
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      onClick={() => setNewTokenOpen(false)}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}

              {tokensErr && <p className="field-error">{tokensErr}</p>}

              <ul className="os-member-list" role="list" style={{ marginTop: 12 }}>
                {tokensBusy && tokens.length === 0 && (
                  <li className="os-member">
                    <span className="os-minfo">
                      <p className="os-mname">Chargement…</p>
                    </span>
                  </li>
                )}
                {!tokensBusy && tokens.length === 0 && (
                  <li className="os-member os-member-pending">
                    <span className="os-avatar os-avatar-pending" aria-hidden="true">
                      ·
                    </span>
                    <div className="os-minfo">
                      <p className="os-mname os-muted-italic">Aucun jeton</p>
                      <p className="os-mmail">Créez-en un pour automatiser des actions.</p>
                    </div>
                  </li>
                )}
                {tokens.map((t) => (
                  <li key={t.id} className="os-member">
                    <span className="os-avatar" aria-hidden="true">
                      🔑
                    </span>
                    <div className="os-minfo">
                      <p className="os-mname">{t.name}</p>
                      <p className="os-mmail">
                        {t.scopes.join(', ')} · créé {fmtDate(t.createdAt)}
                        {t.lastUsedAt ? ` · utilisé ${fmtDate(t.lastUsedAt)}` : ' · jamais utilisé'}
                      </p>
                    </div>
                    <span className="os-mrole">Actif</span>
                    <div className="os-mactions">
                      <button
                        type="button"
                        className="btn-ghost btn-sm os-dangerlink"
                        onClick={() => void revokeToken(t.id)}
                        disabled={tokensBusy}
                      >
                        Révoquer
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>

            <Section id="danger" eyebrow="05" title="Zone dangereuse" isMobile={isMobile}>
              <div className="os-danger-card">
                <div>
                  <h3 className="os-danger-title">Supprimer mon compte</h3>
                  <p className="os-danger-sub">
                    Votre compte, vos listes, cadeaux et participations seront définitivement
                    effacés. <strong>Cette action est irréversible.</strong>
                  </p>
                </div>

                {!dangerOpen ? (
                  <button type="button" className="btn-danger" onClick={() => setDangerOpen(true)}>
                    Supprimer mon compte
                  </button>
                ) : (
                  <div className="os-danger-confirm">
                    <p className="os-danger-prompt">
                      Pour confirmer, tapez votre email : <code>{email}</code>
                    </p>
                    <input
                      className={`input os-mono ${deleteConfirm && !deleteMatches ? 'os-input-err' : ''}`}
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      placeholder={email}
                      autoFocus
                    />
                    {deleteErr && <p className="field-error">{deleteErr}</p>}
                    <div className="os-danger-actions">
                      <button
                        type="button"
                        className="btn-ghost btn-sm"
                        onClick={() => {
                          setDangerOpen(false)
                          setDeleteConfirm('')
                          setDeleteErr(null)
                        }}
                        disabled={deleteBusy}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        className="btn-danger btn-sm"
                        disabled={!deleteMatches || deleteBusy}
                        onClick={() => void deleteAccount()}
                      >
                        {deleteBusy ? 'Suppression…' : 'Supprimer définitivement'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Section>

            <p className="os-bottom">Astuce : révisez vos jetons régulièrement.</p>
          </div>
        </main>
      </div>
    </div>
  )
}
